import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProjects, deleteProject } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search,
  Calendar,
  MapPin,
  Trash2,
  Edit,
  Eye,
  FolderHeart,
  Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const STATUS_MAP = { 0: 'Active', 1: 'Finish', 2: 'Pending' };
const STATUS_STYLES = {
  0: 'bg-green-50 text-green-700 border border-green-200',
  1: 'bg-blue-50 text-blue-700 border border-blue-200',
  2: 'bg-amber-50 text-amber-700 border border-amber-200'
};

export const ProjectsPage = () => {
  const { isLeader } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteProject(deleteId);
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setDeleteId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.bride_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.groom_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === parseInt(statusFilter);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-imperial">Wedding Projects</h1>
          <p className="font-body text-muted-foreground mt-1">
            {isLeader() ? 'Manage all wedding projects' : 'Your assigned projects'}
          </p>
        </div>
        {isLeader() && (
          <Button 
            onClick={() => navigate('/projects/new')}
            className="bg-imperial hover:bg-imperial/90"
            data-testid="create-project-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 bg-white border-warm-grey"
            data-testid="search-projects"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-12 w-40 bg-white border-warm-grey" data-testid="status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="0">Active</SelectItem>
            <SelectItem value="1">Finish</SelectItem>
            <SelectItem value="2">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="card-luxury">
          <CardContent className="p-12 text-center">
            <FolderHeart className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="font-body text-muted-foreground">
              {searchTerm ? 'No projects match your search' : 'No projects found'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="card-luxury"
              data-testid={`project-${project.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-heading text-lg text-foreground">
                    {project.bride_name} & {project.groom_name}
                  </CardTitle>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[project.status] || STATUS_STYLES[0]}`}
                    data-testid={`project-status-${project.id}`}
                  >
                    {STATUS_MAP[project.status] || 'Active'}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="font-body text-sm">{formatDate(project.wedding_date)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span className="font-body text-sm truncate">{project.location}</span>
                </div>
                
                <div className="pt-4 border-t border-warm-grey space-y-2">
                  <div className="flex justify-between">
                    <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Target</span>
                    <span className="font-body text-sm font-medium">{formatCurrency(project.target_budget)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Estimated</span>
                    <span className="font-body text-sm">{formatCurrency(project.total_estimated || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-xs uppercase tracking-widest text-muted-foreground">Realized</span>
                    <span className="font-body text-sm font-medium text-imperial">{formatCurrency(project.total_realized || 0)}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline"
                    className="flex-1 border-imperial text-imperial hover:bg-imperial hover:text-white"
                    onClick={() => navigate(`/projects/${project.id}`)}
                    data-testid={`view-project-${project.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  {isLeader() && (
                    <>
                      <Button 
                        variant="outline"
                        size="icon"
                        onClick={() => navigate(`/projects/${project.id}/edit`)}
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        size="icon"
                        className="text-destructive hover:bg-destructive hover:text-white"
                        onClick={() => setDeleteId(project.id)}
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently delete the project and all associated budget items. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPage;
